import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import PrivateRoute from 'components/AppGuards/PrivateRoute';
import AuthRouter from 'routes/AuthRoutes';
import * as urls from 'routes/config';
import AllowToAgencyUser from 'components/AppGuards/AllowToAgencyUser';
import AllowBehaviourEditable from 'components/AppGuards/AllowBehaviourEditable';
import OnboardLayout from 'components/Layouts/OnboardLayout';
import AppLayout from 'components/Layouts/AppLayout';
import AgencyLayout from 'components/Layouts/AgencyLayout';
import BusinessLayout from 'components/Layouts/BusinessLayout';
import WorksheetLayout from 'components/Layouts/WorksheetLayout';

// Lazy load main components for better performance
const PageNotFound = lazy(() => import('pages/Public/Exceptions/PageNotFound'));
const EmbedPreview = lazy(() => import('pages/Public/EmbedPreview'));
const PreviewLink = lazy(() => import('pages/Public/PreviewLink'));

// Layouts are imported eagerly to keep them mounted and avoid re-suspension

// Lazy load main containers
const Profile = lazy(() => import('pages/UserProfile'));
const Home = lazy(() => import('pages/Home/Home'));
const AccessDenied = lazy(() => import('pages/Home/AccessDenied'));
// Import route components eagerly to prevent layout re-mounting
import AgentRoutes from 'routes/Business/Agent';
import AgencyRoutes from 'routes/Agency';
import BusinessSettingRoutes from 'routes/Business/Setting';
import WorksheetRoutes from './Business/Worksheet/Index';
import AddIntegrationForm from '@/pages/Business/Setting/Integrations/Components/AddIntegrationForm';
import BusinessOnboardingLayout from '@/components/Layouts/BusinessOnboardingLayout';
import BusinessOnboardingRoutes from './BusinessOnboardingRoutes';
import NetsapiensLoginRedirect from 'pages/Public/Authentication/NetsapiensLoginRedirect';
import Login from '@/pages/Public/Authentication/Login';

// Lazy load agent components
const CreateAgent = lazy(() => import('pages/Business/Agent/CreateAgent/CreateAgent'));
const SelectAgent = lazy(() => import('pages/Business/Agent/SelectAgent/SelectAgent'));
const PromptBuilder = lazy(() => import('@/pages/Business/Agent/Configure/Behavior/PromptBuilder'));

export default function MainRoutes() {
  return (
    <Suspense fallback={null}>
      <Routes>
        {/* Auth Routes */}
        <Route path={`${urls.auth.base}/*`} element={<AuthRouter />} />
        <Route path='/auth/login' element={<Login />} />
        <Route path='/:cspSlug/auth/login' element={<NetsapiensLoginRedirect />} />
        <Route path='/access-denied' element={<AccessDenied />} />

        <Route
          path='/'
          element={
            <PrivateRoute
              outlet={
                <AppLayout>
                  <Home />
                </AppLayout>
              }
            />
          }
        />

        {/* Public link */}
        <Route path={urls.publicRoutes.embedPreview} element={<EmbedPreview />} />
        <Route path={urls.publicRoutes.previewLink()} element={<PreviewLink />} />

        {/* Create Agent Route */}
        <Route
          path={`${urls.base()}/${urls.onBoarding.create}`}
          element={
            <PrivateRoute
              outlet={
                <OnboardLayout>
                  <CreateAgent />
                </OnboardLayout>
              }
            />
          }
        />

        {/* Select Agent Route */}
        <Route
          path={`${urls.base()}/${urls.onBoarding.selectAgent}`}
          element={
            <PrivateRoute
              outlet={
                <OnboardLayout>
                  <SelectAgent />
                </OnboardLayout>
              }
            />
          }
        />

        {/* Business Routes */}
        <Route
          path={`${urls.business.base}/*`}
          element={
            <PrivateRoute
              outlet={
                <BusinessLayout>
                  <BusinessSettingRoutes />
                </BusinessLayout>
              }
            />
          }
        />

        {/* Agent Routes */}
        <Route
          index
          path={`${urls.agentBase()}/*`}
          element={
            <PrivateRoute
              outlet={
                <AppLayout>
                  <AgentRoutes />
                </AppLayout>
              }
            />
          }
        />

        {/* Prompt Tree Route - No Layout */}
        <Route
          path={`${urls.agentBase()}/configure/behavior/instructions/prompt-tree`}
          element={<PrivateRoute outlet={<AllowBehaviourEditable outlet={<PromptBuilder />} />} />}
        >
          <Route path='integration/add' element={<AddIntegrationForm />} />
        </Route>

        <Route
          index
          path={`${urls.worksheetBase()}/*`}
          element={
            <PrivateRoute
              outlet={
                <WorksheetLayout>
                  <WorksheetRoutes />
                </WorksheetLayout>
              }
            />
          }
        />

        {/* Agency routes */}
        <Route
          index
          path={`${urls.agency.base}/*`}
          element={
            <AllowToAgencyUser
              outlet={
                <AgencyLayout>
                  <AgencyRoutes />
                </AgencyLayout>
              }
            />
          }
        />

        {/* Business Onboarding routes */}
        <Route
          index
          path={`${urls.businessOnboarding.base}/*`}
          element={
            <BusinessOnboardingLayout>
              <BusinessOnboardingRoutes />
            </BusinessOnboardingLayout>
          }
        />

        {/* Profile Route */}
        <Route path={`${urls.base()}/${urls.home.profile}`} element={<PrivateRoute outlet={<Profile />} />} />

        <Route path='/404' element={<PageNotFound />} />
        <Route path='*' element={<Navigate to='/404' replace />} />
      </Routes>
    </Suspense>
  );
}
